/**
 * Notifications Hooks
 *
 * TanStack Query hooks for notifications with real-time updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import { useSocketEventCallback } from '../lib/socket';

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params?: { unreadOnly?: boolean }) =>
    [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

// Response types
export interface Notification {
  id: string;
  orgId: string;
  userId: string | null;
  operatorId: string | null;
  type: NotificationType;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
  isRead: boolean;
  readAt: string | null;
  emailSent: boolean;
  createdAt: string;
}

export type NotificationType =
  | 'training_assigned'
  | 'training_reminder'
  | 'training_overdue'
  | 'assessment_created'
  | 'assessment_score_low'
  | 'sop_processing_complete'
  | 'sop_processing_failed'
  | 'system_announcement';

export interface NotificationPreference {
  type: NotificationType;
  channel: 'in_app' | 'email' | 'both';
  isEnabled: boolean;
}

interface NotificationListResponse {
  data: Notification[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  unreadCount: number;
}

// ============================================
// QUERIES
// ============================================

/**
 * Fetch notifications list
 */
export function useNotifications(params?: { unreadOnly?: boolean; limit?: number }) {
  const api = useApi();
  const queryClient = useQueryClient();

  // Listen for real-time notifications
  useSocketEventCallback('notification:new', () => {
    // Invalidate queries to refetch
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  });

  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params?.limit) searchParams.set('limit', String(params.limit));
      if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
      const query = searchParams.toString();
      return api.get<NotificationListResponse>(`/api/notifications${query ? `?${query}` : ''}`);
    },
  });
}

/**
 * Fetch unread count only
 */
export function useUnreadCount() {
  const api = useApi();
  const queryClient = useQueryClient();

  // Listen for real-time notifications
  useSocketEventCallback('notification:new', () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  });

  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => api.get<{ count: number }>('/api/notifications/unread-count'),
    refetchInterval: 60000, // Also poll every minute as fallback
  });
}

/**
 * Fetch notification preferences
 */
export function useNotificationPreferences() {
  const api = useApi();

  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => api.get<NotificationPreference[]>('/api/notifications/preferences'),
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Mark notification as read
 */
export function useMarkAsRead() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      api.put<{ message: string }>(`/api/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllAsRead() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.put<{ message: string }>('/api/notifications/read-all'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Update notification preference
 */
export function useUpdatePreference() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      type: NotificationType;
      channel: 'in_app' | 'email' | 'both';
      isEnabled?: boolean;
    }) => api.put<{ message: string }>('/api/notifications/preferences', params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
  });
}
